import {
  clearChildChangeSource,
  getChildChangeSource,
} from "@/components/Inputs/ChildrenAgeSelect/childChangeSource.ts";
import { useAgeDropdown } from "@/helpers/hooks/useAgeDropdown.ts";
import { useAutoOpen } from "@/helpers/hooks/useAutoOpen.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { ChildType } from "@/stores/EngineStore/interfaces/ChildType.ts";
import { Baby, ChevronDown } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const ageOptions = Array.from({ length: 18 }, (_, i) => ({
  value: i,
  label: i === 0 ? "< 1" : String(i),
}));

export const ChildAgeDropdown = ({
  child,
  cartItemId,
  compact,
  label,
  autoOpen,
  instanceId,
}: {
  child: ChildType;
  cartItemId: string;
  compact?: boolean;
  label?: string;
  autoOpen?: boolean;
  instanceId?: string;
}) => {
  const { t } = useTranslation();
  const setCartItemChildAge = useEngineStore((state) => state.setCartItemChildAge);
  const [isHighlight, setIsHighlight] = useState(false);

  const {
    isOpen,
    isEditing,
    inputValue,
    highlightedIndex,
    dropdownStyle,
    filteredOptions,
    dropdownRef,
    triggerRef,
    inputRef,
    containerEl,
    open,
    handleSelect: baseHandleSelect,
    handleKeyDown,
    handleInputChange,
    commitTypedValue,
    setHighlightedIndex,
    setIsOpen,
    setIsEditing,
    setInputValue,
    updatePosition,
  } = useAgeDropdown({
    options: ageOptions,
    minAge: 0,
    maxAge: 17,
    onSelect: (age) => {
      setCartItemChildAge(child.id, age, cartItemId);
      setIsHighlight(false);
    },
  });

  const handleSelect = (age: number) => {
    baseHandleSelect(age);
  };

  const shouldAutoOpen =
    !!autoOpen && child.age === null && (!instanceId || getChildChangeSource() === instanceId);

  useAutoOpen(
    shouldAutoOpen,
    () => {
      setIsOpen(true);
      setIsHighlight(true);
      setIsEditing(true);
      setInputValue("");
      updatePosition();
      clearChildChangeSource();
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    200,
  );

  const currentLabel =
    child.age === null ? "—" : ageOptions.find((o) => o.value === child.age)?.label ?? "< 1";

  return (
    <div className={`c-child-age-input${compact ? " c-child-age-input--compact" : ""}`}>
      {!compact && (
        <div className="c-child-age-input__icon">
          <Baby size={12} />
        </div>
      )}
      <label className="c-be-number-input-label">{label || t("childAge")}</label>
      <div
        ref={triggerRef}
        className={`c-child-age-input__select-wrap${isOpen ? " is-open" : ""}${
          isHighlight ? " is-highlight" : ""
        }`}
        onClick={open}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            className="c-child-age-input__input"
            type="text"
            inputMode="numeric"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={commitTypedValue}
            placeholder={currentLabel}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="c-child-age-input__value">{currentLabel}</span>
        )}
        <ChevronDown size={14} className="c-child-age-input__chevron" />
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="c-child-age-input__dropdown"
            style={dropdownStyle}
            onMouseDown={(e) => e.preventDefault()}
          >
            {filteredOptions.map((opt, idx) => (
              <div
                key={opt.value}
                data-value={opt.value}
                className={`c-child-age-input__option${
                  opt.value === (child.age ?? 0) ? " c-child-age-input__option--selected" : ""
                }${idx === highlightedIndex ? " c-child-age-input__option--highlighted" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(opt.value);
                }}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                {opt.label}
              </div>
            ))}
          </div>,
          containerEl,
        )}
    </div>
  );
};
